import { IonBackButton, IonBadge, IonButton, IonButtons, IonCard, IonCardContent, IonContent, IonHeader, IonIcon, IonImg, IonLabel, IonLoading, IonMenuButton, IonPage, IonSpinner, IonTabs, IonTabBar, IonTabButton, IonTitle, IonToolbar, useIonLoading, useIonRouter, useIonViewDidEnter, useIonViewDidLeave, useIonViewWillLeave } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAppContext } from '../AppProvider';
import { Geolocation } from '@capacitor/geolocation';
import { GoogleMap } from '@capacitor/google-maps';
import { Preferences } from '@capacitor/preferences';
import { cloudUploadOutline, eyeOutline, locationOutline, refreshOutline } from 'ionicons/icons';

const Location: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [map, setMap] = useState<any>(null);
    const [currentTime, setCurrentTime] = useState<any>(new Date().toLocaleTimeString('en-US'));
    const [currentLatitude, setCurrentLatitude] = useState<number>();
    const [currentLongitude, setCurrentLongitude] = useState<number>();
    const [currentAccuracy, setCurrentAccuracy] = useState<number>();
    const [currentAltitude, setCurrentAltitude] = useState<number>();
    const [currentAltitudeAccuracy, setCurrentAltitudeAccuracy] = useState<number>();
    const [currentHeading, setCurrentHeading] = useState<number>();
    const [currentSpeed, setCurrentSpeed] = useState<number>();
    const [saving, setSaving] = useState(false);
    const [cachedBreadcrumbs, setCachedBreadcrumbs] = useState<number>(0);

    const dochtml = document.documentElement;
    const docbody = document.body;
    const contentRef = useRef<any>();
    const reloadBtnRef = useRef<any>();
    const savePosBtnRef = useRef<any>();
    const loadingOverlay = useRef<any>();
    const mapRef = useRef<HTMLElement>();
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY!;
    let myMap: GoogleMap;
    
    var timeout:any;

    const { networkStatus, setNetworkStatus, getAppStatus, authKey, authenticateMember, settings, locationPerm, SWTextLogo, SWFullHorizLogo, SWTextDarkLogo, showToast, dismiss } = useAppContext();

    const router = useIonRouter();

    const platform = Capacitor.getPlatform();

    //
    // Authenticate Member
    //
    useIonViewDidEnter(() => {
        const authenticate = async () => {
            const appstat = await getAppStatus();

            if (!networkStatus && appstat === 'No') {
                router.push('/connection', 'forward');
            } else {
                const authenticated = await authenticateMember();   
                if (!authenticated || authenticated.mt == '') router.push('/login', 'forward');
                else {                
                    dochtml.style.background = 'none';
                    docbody.style.background = 'none';
                    //contentRef.current.style.background = 'none';

                    const existing = await Preferences.get({ key: 'cachedBreadcrumbs' });
                    const list = existing.value ? JSON.parse(existing.value) : [];
                    setCachedBreadcrumbs(list.length);
                }
            }
        };
        authenticate();
    }); 


    useEffect(() => {
        if (networkStatus) {
            //console.log('Re-connected');
            saveCachedBreadcrumbs();
        }
    }, [networkStatus]);
    

    //
    // Restore background colors upon leaving
    //
    useIonViewWillLeave(() => {
        dochtml.style.background = 'var(--ion-background-color)';
        docbody.style.background = 'var(--ion-background-color)';
        contentRef.current.style.background = 'var(--ion-background-color)';
    });

    useIonViewDidLeave(() => {
        if (dochtml != undefined) dochtml.style.background = 'var(--ion-background-color)';
        if (docbody != undefined) docbody.style.background = 'var(--ion-background-color)';
        if (contentRef.current != undefined) contentRef.current.style.background = 'var(--ion-background-color)';
    });


    //
    // Initiate Map Loading
    //
    useEffect(() => {
        if(settings !== undefined && map === null) {
            // Set timeout for map loading
            timeout = setTimeout(() => {
                if(map === null) {
                    setLoading(false);
                }
            }, 7500); 
            
            createMap();
        }
    }, [settings]);


    const markerMeta = new Map<number,
                            {
                                coordinate: object;
                                message: string;
                            }
                            >();

    interface MarkerData {
        coordinate: object;
        message: string;
    }


    //
    // Night / Dark Mode Styles
    //
    const nightModeStyles = [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [{ color: "#263c3f" }],
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.fill",
          stylers: [{ color: "#6b9a76" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#38414e" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#212a37" }],
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [{ color: "#9ca5b3" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#746855" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1f2835" }],
        },
        {
          featureType: "road.highway",
          elementType: "labels.text.fill",
          stylers: [{ color: "#f3d19c" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#2f3948" }],
        },
        {
          featureType: "transit.station",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#17263c" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [{ color: "#515c6d" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#17263c" }],
        },
    ];


    //
    // Reload map 
    //
    const reloadMap = async () => {
        setLoading(true);
        if (loadingOverlay.current !== null) loadingOverlay.current.style.display = 'block';
        setTimeout(createMap, 500);
    };


    //
    // Add a marker to the map
    //
    const addMapMarker = async (map:any, latitude:number, longitude:number, time:number) =>  {
        const date = new Date(time);
        const message = date.toLocaleString();
        let markerId = await map.addMarker({
            coordinate: {
                lat: latitude,
                lng: longitude,
            },
            //snippet: 'test',
            //title: message,
            iconUrl: "map_icon.png",   // Custom icon!
            iconSize: {
                width: 32,
                height: 32
            }
        });
    };   

    //
    // Add a marker to the map
    //
    const addMapMarkers = async (map:any, Markers:MarkerData[]) =>  {
        const markers: Array<any> = [];
        const messages: Array<any> = [];
        const icons: Array<any> = [];
        const sizes: Array<any> = [];
        
        Markers.forEach((current:any, index, array) => {
            let coord = current[0];
            let mssg  = current[1];
            let icon  = current[2];
            let size  = current[3];
            markers.push({coordinate:coord, iconUrl:icon, iconSize:size});   
            messages.push({message:mssg});         
        });

        let markerIds = await map.addMarkers(markers);
        
        markerIds.forEach((id:number, index:number) => {
            const coord = markers[index];  
            const mssg = messages[index];  
            markerMeta.set(id, { coordinate: coord, message: mssg });
        });
    };   


    // Listen for tap and show your own info box
    if (map) {
        /*map.setOnMarkerClickListener(async (event: any) => {
            console.log(event);
            let { markerId, latitude, longitude } = event;
            console.log(markerId, latitude, longitude);

            let latitude_abs = 0;
            if (latitude < 0) {
                latitude_abs = Math.abs(latitude);
                latitude_abs = parseFloat(latitude_abs.toFixed(4));
                latitude = -latitude_abs;
            } else {
                latitude = parseFloat(latitude.toFixed(4));
            }

            let longitude_abs = 0;
            if (longitude < 0) {
                longitude_abs = Math.abs(longitude);
                longitude_abs = parseFloat(longitude_abs.toFixed(4));
                longitude = -longitude_abs;
            } else {
                longitude = parseFloat(longitude.toFixed(2));
            }

            showToast({
                message: `${latitude}, ${longitude}`,
                duration: 3000,
                color: 'secondary',
                swipeGesture: 'vertical',
            });
        });*/

        map.setOnMarkerClickListener(async (event: { markerId: number }) => {
            dismiss();
            const meta:any  = markerMeta.get(event.markerId);            
            if (!meta) return;

            let coord:any = meta.coordinate.coordinate;
            let lat:any = coord.lat;
            let lng:any = coord.lng;
            let mssg:any = meta.message;

            showToast({
                //message: `${lat}, ${lng}, ${mssg.message}`,
                message: `${mssg.message}`,
                duration: 3000,
                color: 'secondary',
                swipeGesture: 'vertical',
            });
        });
    }


    //
    // Create the map
    //}
    const createMap = async () => {
        if (!mapRef.current) return;

        dochtml.style.background = 'none';
        docbody.style.background = 'none';
        //contentRef.current.style.background = 'none';

        let latitude = 0;            
        let longitude = 0; 
        let accuracy = 0; 
        let coords = '';

        try {
            let Coordinates = await getCurrentPosition();
            if(Coordinates !== false && Coordinates.latitude !== undefined && Coordinates.longitude !== undefined) {
                latitude = Coordinates.latitude;
                longitude = Coordinates.longitude;
                accuracy = Coordinates.accuracy;
                coords = latitude + ',' + longitude + ',' + accuracy;
            } 
            setCurrentLatitude(latitude);
            setCurrentLongitude(longitude);
        
            const myMap = await GoogleMap.create({
                id: 'my-location-map',
                element: mapRef.current,
                apiKey: apiKey,
                config: {
                    center: {
                        lat: latitude,
                        lng: longitude
                    },
                    zoom: 17,
                    //gestureHandling: "none",
                    //...(settings.theme !== undefined && settings.theme == '1' && { styles: nightModeStyles })
                }
            });
            
            setMap(myMap);

            clearTimeout(timeout);

            const date = new Date();
            const time = date.getTime();

            await addMapMarker(myMap, latitude, longitude, time);

            if (loadingOverlay.current !== null) loadingOverlay.current.style.display = 'none';

        } catch (e) {
            console.error('Error creating map:', e);
        }
        setLoading(false);
    };


    //
    // Refresh Position
    //
    const refreshPosition = async () => {
        let latitude = 0;            
        let longitude = 0; 
        let accuracy = 0; 
        let coords = '';

        let Coordinates = await getCurrentPosition();
        if(Coordinates !== false && Coordinates.latitude !== undefined && Coordinates.longitude !== undefined) {
            latitude = Coordinates.latitude;
            longitude = Coordinates.longitude;
            accuracy = Coordinates.accuracy;
            coords = latitude + ',' + longitude + ',' + accuracy;
        }
        setCurrentLatitude(latitude);
        setCurrentLongitude(longitude);

        await map.setCamera({
            coordinate: {
                lat: latitude,
                lng: longitude
            },
            animate: true
        });

        const date = new Date();
        const time = date.getTime();

        await addMapMarker(map, latitude, longitude, time);

        return [latitude, longitude, accuracy];
    };


    //
    // Get Current Position
    //
    const getCurrentPosition = async () => {
        try {
            let pos1 = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 7000,
                maximumAge: 0
            });

            // wait briefly for GPS stabilization
            await new Promise(resolve => setTimeout(resolve, 2000));

            let pos2 = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 7000,
                maximumAge: 0
            });

            return (pos2.coords.accuracy < pos1.coords.accuracy)
                ? pos2.coords
                : pos1.coords;
        } catch (e) {
            return false;
        }
    };


    //
    // Save Position
    //
    const savePosition = async () => {
        setSaving(true);
        
        if (savePosBtnRef.current) savePosBtnRef.current.disabled = true;
        const btnText: any = document.getElementById('breadcrumb-btn-text');
        if (btnText) btnText.innerHTML = 'Dropping ...';

        let nowTime = new Date().getTime();
            nowTime = Math.round(nowTime / 1000);
        const timeoutMs = 20000;
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeoutMs);
        
        let latitude = 0;            
        let longitude = 0; 
        let accuracy = 0; 
        let coords = '';

        //if (locationPerm) {
            let Coordinates = await getCurrentPosition();
            //console.log(Coordinates);
            
            if(Coordinates !== false && Coordinates.latitude !== undefined && Coordinates.longitude !== undefined) {
                latitude = Coordinates.latitude;
                longitude = Coordinates.longitude;
                accuracy = Coordinates.accuracy;
                coords = latitude + ',' + longitude + ',' + accuracy;
            }
        //}
        
        const formData = new FormData();
        formData.append('mt', authKey.mt);
        formData.append('lat', latitude.toString());
        formData.append('lon', longitude.toString());
        formData.append('acc', accuracy.toString());   
        
        const breadcrumb = [nowTime, authKey.mt, latitude.toString(), longitude.toString(), accuracy.toString()];

        try {
            
            const response = await fetch('https://api.besolowise.com/tracks/insert/', {
                method: 'POST',
                credentials: 'include',
                body: formData,
                signal: controller.signal,
            });

            if(response.ok) {
                const obj = await response.json(); 
                
                if(obj.success) {
                    if (btnText) savePosBtnRef.current.color = 'success';
                    if (btnText) btnText.innerHTML = 'Saved to server';
                    if (savePosBtnRef.current) savePosBtnRef.current.disabled = false;
                    
                    showToast({
                        message: 'Your location was saved.',
                        duration: 2000,
                        color: 'success',
                    });

                    // 
                    // Try sending cached breadcrumbs
                    //
                    
                    if (cachedBreadcrumbs > 0) await saveCachedBreadcrumbs();

                } else {
                    if (savePosBtnRef.current) savePosBtnRef.current.color = 'danger';
                    if (savePosBtnRef.current) savePosBtnRef.current.disabled = false;
                    
                    showToast({
                        message: 'Error: ' + obj.message,
                        duration: 3000,
                        color: 'danger',
                    });
                } 

                setTimeout(() => {
                    if (savePosBtnRef.current) savePosBtnRef.current.color = 'tertiary';
                    if (btnText) btnText.innerHTML = 'Drop a Breadcrumb';
                }, 2000);
                
            } else {
                //throw new Error(`Server responded with ${response.status}`);

                if (savePosBtnRef.current) savePosBtnRef.current.color = 'danger';
                if (savePosBtnRef.current) savePosBtnRef.current.disabled = false;
                setTimeout(() => {
                    //savePosBtnRef.current.color = 'tertiary';
                    if (btnText) btnText.innerHTML = 'Drop a Breadcrumb';
                }, 2500);

                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                });
            } 

        } catch(error) {
            console.log(error);
            clearTimeout(id);
            await cacheBreadcrumb(breadcrumb); 
        }   
        
        setSaving(false);
    };


    //
    // Locally cache Breadcrumb
    //
    const cacheBreadcrumb = async (breadcrumb:any) => {
        setNetworkStatus(false);
        
        const btnText: any = document.getElementById('breadcrumb-btn-text');
        if (savePosBtnRef.current) savePosBtnRef.current.color = 'danger';
        if (btnText) btnText.innerHTML = 'No connection';

        console.log('Caching breadcrumb...');

        const existing = await Preferences.get({ key: 'cachedBreadcrumbs' });
        const list = existing.value ? JSON.parse(existing.value) : [];

        list.push(breadcrumb);

        await Preferences.set({
            key: 'cachedBreadcrumbs',
            value: JSON.stringify(list),
        });       

        setTimeout(() => {
            if (btnText) btnText.innerHTML = 'Drop a Breadcrumb';
            //savePosBtnRef.current.color = 'tertiary';
            if (savePosBtnRef.current) savePosBtnRef.current.disabled = false; 
            setCachedBreadcrumbs(list.length);
        }, 1500);
    };


    //
    // Save cached Breadcrumbs
    //
    const saveCachedBreadcrumbs = async () => {
        let nowTime = new Date().getTime();
            nowTime = Math.round(nowTime / 1000);
        const timeoutMs = 3000;
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeoutMs);

        const existing = await Preferences.get({ key: 'cachedBreadcrumbs' });
        
        if (cachedBreadcrumbs > 0) {
            const list: any = existing.value ? JSON.parse(existing.value) : [];

            const formDataBatch = new FormData();
            formDataBatch.append('mt', authKey.mt);
            formDataBatch.append('tracks', JSON.stringify(list));
            
            try {
                const response = await fetch('https://api.besolowise.com/tracks/insert-batch/', {
                    method: 'POST',
                    credentials: 'include',
                    body: formDataBatch,
                    signal: controller.signal,
                });

                if(response.ok) {
                    const obj = await response.json(); 
                    
                    if(obj.success) {
                        console.log('All cached breadcrumbs were dropped.');
                        await Preferences.remove({ key: 'cachedBreadcrumbs' });
                        setCachedBreadcrumbs(0);
                    } else {
                        console.log('Connection Error.');
                    }
                } else {
                    console.log('No Connection');
                }
            } catch(error) {
                clearTimeout(id);
                console.log('Could not save cached Breadcrumbs (' + error + ')');
            }
        }
    };


    //
    // Show Saved Positions
    //
    const showSavedPositions = async () => {
        const formData = new FormData();
        formData.append('mt', authKey.mt);
        
        try {
            const response = await fetch('https://api.besolowise.com/tracks/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json(); 
                
                if(obj.length > 0) {
                    let Markers:Array<any> = [];
                    obj.forEach((e:any) => {
                        //addMapMarker(map, parseFloat(e.trackLat), parseFloat(e.trackLon), e.trackTime*1000);
                        const date = new Date(e.trackTime*1000);
                        const message = date.toLocaleString();
                        const coordinate = { lat: parseFloat(e.trackLat), lng: parseFloat(e.trackLon) };
                        const iconURL = 'map_icon.png';
                        const iconSize = { width: 32, height: 32 };
                        
                        //Markers.push([parseFloat(e.trackLat), parseFloat(e.trackLon), message]);
                        Markers.push([coordinate, message, iconURL, iconSize]);
                    });                    

                    addMapMarkers(map, Markers);

                } else {
                    showToast({
                        message: 'No locations found in past 7 days',
                        duration: 2500,
                        color: 'secondary',
                    });
                } 
            } else { 
                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                });
            } 
        } catch(error) {
            showToast({
                message: 'Connection Error: ' + error + '.',
                duration: 3000,
                color: 'danger',
            });
        }                    
    };        

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonBackButton color={'tertiary'}>Back</IonBackButton>
                    </IonButtons>
                    <IonTitle><IonImg className='headerTextLogo' src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent id='LocationContent' ref={contentRef}>

                {loading && map === null &&
                    (<div className='loading-overlay' ref={loadingOverlay}>
                        <IonCard>
                            <IonCardContent className='ion-text-center'>
                                <IonSpinner name="dots"></IonSpinner>
                                <p>Loading map</p>
                            </IonCardContent>
                        </IonCard>
                    </div>)
                }

                {!loading && map === null &&
                    (<div className='loading-overlay' ref={loadingOverlay}>
                        <IonCard>
                            <IonCardContent className='ion-text-center'>
                                <p>Failed to load map.</p>
                                <p>Please check your internet connection.</p>
                            </IonCardContent>
                        </IonCard>
                    </div>)
                }
                
                    <IonTabBar color={'light'}>
                        <IonTabButton 
                            disabled={loading}
                            tab="tab1" 
                            onClick={reloadMap}
                            ref={reloadBtnRef}>
                            <IonIcon icon={refreshOutline} />
                            <IonLabel>Reload</IonLabel>
                        </IonTabButton>
                        <IonTabButton tab="tab2" onClick={refreshPosition}>
                            <IonIcon icon={locationOutline} />
                            <IonLabel>Update</IonLabel>
                        </IonTabButton>
                        <IonTabButton 
                            disabled={saving}
                            tab="tab3" 
                            onClick={savePosition}
                            ref={savePosBtnRef}>
                            <IonIcon icon={cloudUploadOutline} />
                            <IonLabel>Save</IonLabel>
                            {cachedBreadcrumbs >= 1 &&
                                <IonBadge color="danger">{cachedBreadcrumbs}</IonBadge>
                            }
                        </IonTabButton>
                        <IonTabButton tab="tab4" onClick={showSavedPositions}>
                            <IonIcon icon={eyeOutline} />
                            <IonLabel>Show</IonLabel>
                        </IonTabButton>
                    </IonTabBar>
                

                <capacitor-google-map ref={mapRef} style={{
                    display: 'inline-block',
                    width: '100%',
                    height: '90%',
                    zIndex: 9999
                    }}>
                </capacitor-google-map>               

            </IonContent>            

        </IonPage>
    );
};

export default Location;